import { FeatureCylinder3D } from 'features/assetViz/objects/features/procedural/FeatureCylinder3D'
import { FeatureLabel3D } from 'features/assetViz/objects/features/procedural/FeatureLabel3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from 'features/assetViz/objects/features/procedural/SurfaceLineFeature3D'
import {
  SurfaceMeshFeature3D,
  SurfaceMeshSelectionType,
} from 'features/assetViz/objects/features/procedural/SurfaceMeshFeature3D'
import { SurfaceFeature3D } from 'features/assetViz/objects/features/SurfaceFeature3D'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentPolygon,
  GeckoShipDeckDoor,
} from 'types'

/**
 * A component representing a door on the surface of a ship deck component
 * @param model             The door model (from the asset model)
 * @constructor
 */
export const DeckDoorFeature3D: React.FC<{
  model: GeckoShipDeckDoor
}> = ({ model }) => {
  const { bounds, wrapMode } = useSurfaceProps()

  const coords = model.coordinates

  const xCoords = coords.map((point) => point.x.num)
  const yCoords = coords.map((point) => point.y.num)

  // create doorknob positioning, position on long side
  const { knobPosition, knobRadius } = React.useMemo(() => {
    const edge1 = Math.sqrt(
      (xCoords[0] - xCoords[1]) ** 2 + (yCoords[0] - yCoords[1]) ** 2
    )
    const edge2 = Math.sqrt(
      (xCoords[1] - xCoords[2]) ** 2 + (yCoords[1] - yCoords[2]) ** 2
    )
    const slope1 = (yCoords[1] - yCoords[0]) / (xCoords[1] - xCoords[0])
    const slope2 = (yCoords[2] - yCoords[1]) / (xCoords[2] - xCoords[1])

    const lengthSlope = edge1 > edge2 ? slope1 : slope2
    const doorWidth = Math.min(edge1, edge2)

    const knobRadius = doorWidth * 0.25

    // get angle of rotation based on 4 points using principal axis method (PCA)
    const cx = xCoords.reduce((acc, current) => acc + current, 0) / 4.0
    const cy = yCoords.reduce((acc, current) => acc + current, 0) / 4.0
    let Sxx = 0.0
    let Syy = 0.0
    let Sxy = 0.0

    for (let i = 0; i < xCoords.length; i++) {
      const X = xCoords[i] - cx
      const Y = yCoords[i] - cy
      Sxx += X * X
      Syy += Y * Y
      Sxy += X * Y
    }

    const theta = 0.5 * Math.atan2(2 * Sxy, Sxx - Syy)

    const opposite = 0.5 * doorWidth * Math.sin(theta)
    const adjacent = 0.5 * doorWidth * Math.cos(theta)

    let knobX, knobY

    // positive slope, then orientation of points differ (SE together, and NW together)
    if (lengthSlope >= 0) {
      switch (model.direction) {
        case 'north':
          knobX = Math.abs(adjacent)
          knobY = Math.abs(opposite)
          break
        case 'east':
          knobX = Math.abs(opposite) * -1
          knobY = Math.abs(adjacent) * -1
          break
        case 'west':
          knobX = Math.abs(opposite)
          knobY = Math.abs(adjacent)
          break
        case 'south':
          knobX = Math.abs(opposite) * -1
          knobY = Math.abs(adjacent) * -1
          break
        default: // center X,Y
          knobX = 0
          knobY = 0
      }
      // negative slope, then orientation of points differ (NE together, and SW together)
    } else {
      switch (model.direction) {
        case 'north':
          knobX = Math.abs(opposite) * -1
          knobY = Math.abs(adjacent)
          break
        case 'east':
          knobX = Math.abs(opposite) * -1
          knobY = Math.abs(adjacent)
          break
        case 'west':
          knobX = Math.abs(opposite)
          knobY = Math.abs(adjacent) * -1
          break
        case 'south':
          knobX = Math.abs(opposite)
          knobY = Math.abs(adjacent) * -1
          break
        default: // center X,Y
          knobX = 0
          knobY = 0
      }
    }

    const knobPosition: [number, number, number] = [knobX, 0, knobY]
    return { knobPosition, knobRadius }
  }, [model.direction, xCoords, yCoords])

  // creating labelling coordinates and positioning
  const { labelCoords, positionLabelLeft } = React.useMemo(() => {
    const positionLabelLeft =
      model.coordinates[0].x.num <
      bounds.start.x.plus(bounds.end.x).scale(0.5).num

    const width = model.coordinates[0].x
      .minus(model.coordinates[2].x)
      .scale(0.5)
      .abs()
    const labelX = positionLabelLeft
      ? model.coordinates[0].x.minus(width)
      : model.coordinates[0].x.plus(width)

    const labelY = model.coordinates[0].y
      .plus(model.coordinates[2].y)
      .scale(0.5)

    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      labelX,
      labelY
    )
    return { labelCoords, positionLabelLeft }
  }, [model, bounds])

  // create door frame
  const boundingPath = React.useMemo(
    () =>
      GeckoComponentPolygon.CreateFromPolygonalPlate(
        model.coordinates,
        bounds,
        wrapMode
      ),
    [bounds, model.coordinates, wrapMode]
  )

  const centerGeometry = React.useMemo(() => {
    const centerX = (Math.max(...xCoords) + Math.min(...xCoords)) / 2
    const centerY = (Math.max(...yCoords) + Math.min(...yCoords)) / 2
    const firstPoint = coords[0]

    return GeckoComponentCoords.CreateFromValues(
      bounds.componentID,
      centerX,
      firstPoint.x.unit,
      centerY,
      firstPoint.y.unit
    )
  }, [bounds.componentID, coords, xCoords, yCoords])

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelLeft ? 'right' : 'left'}
      />

      <SurfaceLineFeature3D
        path={boundingPath}
        featureModel={model}
        lineSelectionType={SurfaceLineSelectionType.SelectableFeatureLine}
      />
      <SurfaceMeshFeature3D
        path={boundingPath}
        featureModel={model}
        selectionType={SurfaceMeshSelectionType.AssetModelEditorSelectable}
        colorMesh={true}
        fillColor={'white'}
      />

      <SurfaceFeature3D model={{ ...model, geometry: centerGeometry }}>
        <FeatureCylinder3D
          feature={model}
          position={knobPosition}
          radius={knobRadius}
          height={0.05}
          debugColor={'silver'}
          color={'white'}
        />
      </SurfaceFeature3D>
    </>
  )
}
